import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from '@apollo/client';
import { ArrowRight, Camera, Loader2, Mail, MessageCircle, User, ZoomIn, ZoomOut } from 'lucide-react';
import PROXI_BRAIN_LOGO from '@/ui/assets/images/proxivideobrainlogo.png';
import {
  SAVE_PERSONAL_DATA,
  GET_SIGNED_UPLOAD_URL,
  UPDATE_ZIP_CODE,
} from '@/api/graphql/mutations';
import { CURRENT_USER } from '@/api/graphql/queries';
import { LightShell, LightMark } from '../light-ui';
import { auth as firebaseAuth } from '@/lib/firebase';
import { hasEstablishedChannel, hasContributorProfileBasics } from '@/lib/contributorProfile';
import type { User as ApiUser } from '@/types/api';

interface ContributorOnboardingProps {
  open: boolean;
  onComplete: () => void;
  userName?: string;
  /** E.164 phone the user just verified during signup. When present, the SMS
   *  branch of step 1 shows consent only — no phone input. */
  verifiedPhone?: string;
  /** Already-loaded profile of the signed-in user. Used to prefill every
   *  field and to start the flow at the first step whose data is actually
   *  missing — a contributor who already picked a channel and saved their
   *  profile elsewhere (app onboarding, SMS opt-in, Studio import) should
   *  never be asked for the same answers again. */
  currentUser?: ApiUser | null;
}

type InterviewChannel = 'SMS' | 'APP';

const CROP_CONTAINER_SIZE = 240;
const CROP_OUTPUT_SIZE = 512;

const ContributorOnboarding: React.FC<ContributorOnboardingProps> = ({
  open,
  onComplete,
  userName,
  verifiedPhone,
  currentUser,
}) => {
  const savedProfile = currentUser?.personalData;

  // Auth-derived defaults. Firebase auth is the source of truth for
  // already-verified contact methods; the saved profile fills the rest in.
  const authPhone = useMemo(
    () => verifiedPhone || firebaseAuth.currentUser?.phoneNumber || savedProfile?.phone || '',
    [verifiedPhone, savedProfile?.phone],
  );
  const authEmail = useMemo(() => firebaseAuth.currentUser?.email ?? '', []);
  const authDisplayName = firebaseAuth.currentUser?.displayName ?? '';

  // Steps whose data already exists are skipped outright: a contributor who
  // has a channel/consent on file and a saved profile starts on the photo
  // step instead of re-answering everything.
  const channelAlreadySet = hasEstablishedChannel(currentUser);
  const profileAlreadySet = hasContributorProfileBasics(currentUser);
  const [step, setStep] = useState<1 | 2 | 3>(() => {
    if (!channelAlreadySet) return 1;
    return profileAlreadySet ? 3 : 2;
  });

  // Step 1 — Channel selection (prefilled from the saved profile)
  const [channel, setChannel] = useState<InterviewChannel>(() =>
    savedProfile?.preferredInterviewChannel === 'APP' ? 'APP' : 'SMS',
  );
  const [phone, setPhone] = useState(authPhone || '');
  const [smsConsent, setSmsConsent] = useState(savedProfile?.smsConsent?.granted === true);

  // Step 2 — Profile info (prefilled from the saved profile, then the social
  // displayName, so returning users and OAuth signups can click through
  // without retyping).
  const [firstName, setFirstName] = useState(() => {
    if (savedProfile?.firstName) return savedProfile.firstName;
    const parts = authDisplayName.trim().split(/\s+/);
    return parts[0] ?? '';
  });
  const [lastName, setLastName] = useState(() => {
    if (savedProfile?.lastName) return savedProfile.lastName;
    const parts = authDisplayName.trim().split(/\s+/);
    return parts.length > 1 ? parts.slice(1).join(' ') : '';
  });
  const [occupation, setOccupation] = useState(savedProfile?.occupation ?? '');
  const [zipCode, setZipCode] = useState(savedProfile?.zipCode ?? '');
  const [description, setDescription] = useState(savedProfile?.bio ?? '');

  // Step 3 — Photo
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [photoFile, setPhotoFile] = useState<File | null>(null);

  const [rawPhotoUrl, setRawPhotoUrl] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState<{ x: number; y: number } | null>(null);
  const [dragStartOffset, setDragStartOffset] = useState({ x: 0, y: 0 });
  const [imgDimensions, setImgDimensions] = useState({ width: 0, height: 0 });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // The component stays mounted with open=false while the page's auth/user
  // queries resolve, so the useState initializers above may have run before
  // `currentUser` arrived. Re-seed from the saved profile at the moment the
  // modal actually opens (false→true transition only, so it never clobbers
  // anything the user is mid-typing).
  const prevOpenRef = useRef(open);
  useEffect(() => {
    const justOpened = open && !prevOpenRef.current;
    prevOpenRef.current = open;
    if (!justOpened) return;
    const pd = savedProfile;
    if (pd?.preferredInterviewChannel === 'APP') setChannel('APP');
    if (pd?.smsConsent?.granted === true) setSmsConsent(true);
    if (pd?.phone) setPhone(prev => prev || pd.phone!);
    if (pd?.firstName) setFirstName(pd.firstName);
    if (pd?.lastName) setLastName(pd.lastName);
    if (pd?.occupation) setOccupation(prev => prev || pd.occupation!);
    if (pd?.zipCode) setZipCode(prev => prev || pd.zipCode!);
    if (pd?.bio) setDescription(prev => prev || pd.bio!);
    setStep(!channelAlreadySet ? 1 : profileAlreadySet ? 3 : 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const [savePersonalData] = useMutation(SAVE_PERSONAL_DATA);
  const [updateZipCode] = useMutation(UPDATE_ZIP_CODE);
  const [getSignedUploadUrl] = useMutation(GET_SIGNED_UPLOAD_URL);

  const handleZoomChange = useCallback(
    (newZoom: number) => {
      setZoom(newZoom);
      setOffset(prev => {
        const s = CROP_CONTAINER_SIZE / Math.min(imgDimensions.width, imgDimensions.height);
        const maxX = Math.max(0, (imgDimensions.width * s * newZoom - CROP_CONTAINER_SIZE) / 2);
        const maxY = Math.max(0, (imgDimensions.height * s * newZoom - CROP_CONTAINER_SIZE) / 2);
        return { x: Math.max(-maxX, Math.min(maxX, prev.x)), y: Math.max(-maxY, Math.min(maxY, prev.y)) };
      });
    },
    [imgDimensions],
  );

  if (!open) return null;

  const isStep1Valid =
    channel === 'APP' ||
    (channel === 'SMS' && (!!authPhone || phone.trim().length >= 7) && smsConsent);

  const isStep2Valid =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    occupation.trim().length > 0 &&
    zipCode.trim().length > 0 &&
    description.trim().length > 0;

  const handleStep1Submit = async () => {
    if (!isStep1Valid) return;
    setError(null);
    setIsSubmitting(true);
    try {
      const fields: Record<string, unknown> = {
        preferredInterviewChannel: channel,
      };
      if (channel === 'SMS') {
        const trimmedPhone = phone.trim();
        if (trimmedPhone) fields.phone = trimmedPhone;
        fields.smsConsent = {
          granted: smsConsent,
          grantedAt: smsConsent ? new Date().toISOString() : undefined,
          source: 'web_train_link_onboarding',
          status: smsConsent ? 'CONFIRMED' : 'DECLINED',
        };
      }
      await savePersonalData({
        variables: { fields },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
      });
      setStep(2);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save your channel. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleStep2Submit = async () => {
    if (!isStep2Valid) return;
    setError(null);
    setIsSubmitting(true);
    try {
      await savePersonalData({
        variables: {
          fields: {
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            fullName: `${firstName.trim()} ${lastName.trim()}`,
            occupation: occupation.trim(),
            zipCode: zipCode.trim(),
            bio: description.trim(),
          },
        },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
      });
      await updateZipCode({
        variables: { zipCode: zipCode.trim() },
        update: (cache, { data }) => {
          if (data?.updateZipCode?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.updateZipCode.user } });
          }
        },
      });
      setStep(3);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save your info. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePhotoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { setError('Please select an image file.'); return; }
    if (file.size > 5 * 1024 * 1024) { setError('Image must be under 5MB.'); return; }
    setError(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      setRawPhotoUrl(dataUrl);
      const img = new Image();
      img.onload = () => {
        setImgDimensions({ width: img.naturalWidth, height: img.naturalHeight });
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setShowCropper(true);
      };
      img.src = dataUrl;
    };
    reader.readAsDataURL(file);
  };

  const handleCropConfirm = () => {
    if (!rawPhotoUrl) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = CROP_OUTPUT_SIZE;
      canvas.height = CROP_OUTPUT_SIZE;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      const s = CROP_CONTAINER_SIZE / Math.min(img.width, img.height);
      const visibleSize = CROP_CONTAINER_SIZE / (s * zoom);
      const cx = img.width / 2 - offset.x / (s * zoom);
      const cy = img.height / 2 - offset.y / (s * zoom);
      ctx.drawImage(
        img,
        cx - visibleSize / 2, cy - visibleSize / 2,
        visibleSize, visibleSize,
        0, 0, CROP_OUTPUT_SIZE, CROP_OUTPUT_SIZE,
      );
      canvas.toBlob((blob) => {
        if (blob) {
          setPhotoFile(new File([blob], 'avatar.jpg', { type: 'image/jpeg' }));
          setPhotoPreview(canvas.toDataURL('image/jpeg', 0.9));
          setShowCropper(false);
        }
      }, 'image/jpeg', 0.9);
    };
    img.src = rawPhotoUrl;
  };

  const getClientPos = (e: React.MouseEvent | React.TouchEvent) => {
    if ('touches' in e) return { x: e.touches[0].clientX, y: e.touches[0].clientY };
    return { x: e.clientX, y: e.clientY };
  };

  const handleCropDragStart = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    const pos = getClientPos(e);
    setDragStart(pos);
    setDragStartOffset({ ...offset });
  };

  const handleCropDragMove = (e: React.MouseEvent | React.TouchEvent) => {
    if (!dragStart) return;
    e.preventDefault();
    const pos = getClientPos(e);
    const s = CROP_CONTAINER_SIZE / Math.min(imgDimensions.width, imgDimensions.height);
    const maxX = Math.max(0, (imgDimensions.width * s * zoom - CROP_CONTAINER_SIZE) / 2);
    const maxY = Math.max(0, (imgDimensions.height * s * zoom - CROP_CONTAINER_SIZE) / 2);
    const newX = Math.max(-maxX, Math.min(maxX, dragStartOffset.x + (pos.x - dragStart.x)));
    const newY = Math.max(-maxY, Math.min(maxY, dragStartOffset.y + (pos.y - dragStart.y)));
    setOffset({ x: newX, y: newY });
  };

  const handleCropDragEnd = () => setDragStart(null);

  const uploadPhoto = async (): Promise<string | null> => {
    if (!photoFile) return null;
    const ext = photoFile.name.split('.').pop() || 'jpg';
    const fileName = `avatars/${Date.now()}.${ext}`;
    const { data } = await getSignedUploadUrl({ variables: { fileName, contentType: photoFile.type } });
    if (!data?.getSignedUploadUrl) throw new Error('Failed to get upload URL');
    const uploadUrl = data.getSignedUploadUrl;
    await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': photoFile.type, 'x-amz-acl': 'public-read' },
      body: photoFile,
    });
    return uploadUrl.split('?')[0];
  };

  const handleComplete = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      let avatarUrl: string | null = null;
      if (photoFile) avatarUrl = await uploadPhoto();
      const fields: Record<string, unknown> = { contributorOnboardingFinished: true };
      if (avatarUrl) fields.avatar = avatarUrl;
      await savePersonalData({
        variables: { fields },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
      });
      onComplete();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipPhoto = async () => {
    setError(null);
    setIsSubmitting(true);
    try {
      await savePersonalData({
        variables: { fields: { contributorOnboardingFinished: true } },
        update: (cache, { data }) => {
          if (data?.savePersonalData?.user) {
            cache.writeQuery({ query: CURRENT_USER, data: { me: data.savePersonalData.user } });
          }
        },
        refetchQueries: [{ query: CURRENT_USER }],
        awaitRefetchQueries: true,
      });
      onComplete();
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to save. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const stepDot = (active: boolean) => (
    <span
      style={{
        width: 32, height: 3,
        borderRadius: 2,
        background: active
          ? 'linear-gradient(90deg, var(--lp-violet), var(--lp-pink), var(--lp-orange))'
          : 'var(--lp-line)',
        transition: 'background 280ms ease',
      }}
    />
  );

  const heading =
    step === 1
      ? 'How should we send you new interviews?'
      : step === 2
        ? 'Tell us about yourself.'
        : "Add your Proxi's photo.";
  const subhead =
    step === 1
      ? `${userName ? `Welcome ${userName}! ` : ''}Pick a channel. You can change this in your account later.`
      : step === 2
        ? "Let's set up your contributor profile."
        : "This is how you'll appear on our shows. Use a clear, front-facing photo. You can change it later.";

  const channelTile = (value: InterviewChannel, title: string, body: string, Icon: typeof MessageCircle) => {
    const selected = channel === value;
    return (
      <button
        type="button"
        onClick={() => { setChannel(value); setError(null); }}
        disabled={isSubmitting}
        className="lp-tile"
        data-selected={selected}
        style={{ width: '100%', textAlign: 'left' }}
      >
        <span style={{ width: 36, height: 36, borderRadius: 10, background: 'rgba(0,0,0,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon style={{ width: 16, height: 16, color: 'var(--lp-ink)' }} />
        </span>
        <span className="lp-tile-text">
          <span style={{ fontWeight: 600, color: 'var(--lp-ink)' }}>{title}</span>
          <span className="lp-tile-hint">{body}</span>
        </span>
      </button>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1150 }} role="dialog" aria-modal="true" aria-label="Contributor onboarding">
      <LightShell scroll>
        <main
          style={{
            minHeight: '100dvh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'calc(env(safe-area-inset-top, 0px) + 24px) clamp(16px, 4vw, 32px) calc(env(safe-area-inset-bottom, 0px) + 48px)',
          }}
        >
          <div className="lp-card" style={{ width: '100%', maxWidth: 500, padding: 'clamp(24px, 4vw, 36px)' }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: 12 }}>
              <LightMark src={PROXI_BRAIN_LOGO} size={56} />
              <h2 className="lp-section-heading" style={{ fontSize: 'clamp(22px, 3.4vw, 28px)' }}>
                {heading}
              </h2>
              <p className="lp-prose" style={{ fontSize: 14, maxWidth: 380 }}>
                {subhead}
              </p>
              <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                {stepDot(step >= 1)}
                {stepDot(step >= 2)}
                {stepDot(step >= 3)}
              </div>
            </div>

            {error && <div className="lp-error" style={{ marginTop: 16 }}>{error}</div>}

            {/* Step 1 — Notification channel */}
            {step === 1 && (
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
                {channelTile('SMS', 'Text messages', 'Get a text when a new interview is ready', MessageCircle)}

                {channel === 'SMS' && (
                  <>
                    {!authPhone && (
                      <div>
                        <label className="lp-field-label" htmlFor="phone">Phone number</label>
                        <input
                          id="phone"
                          type="tel"
                          inputMode="tel"
                          autoComplete="tel"
                          value={phone}
                          onChange={e => setPhone(e.target.value)}
                          placeholder="+1 555 123 4567"
                          disabled={isSubmitting}
                          className="lp-input"
                        />
                      </div>
                    )}
                    <label className="lp-tile" style={{ alignItems: 'flex-start' }}>
                      <input
                        type="checkbox"
                        checked={smsConsent}
                        onChange={e => setSmsConsent(e.target.checked)}
                        disabled={isSubmitting}
                        style={{ marginTop: 4, accentColor: 'var(--lp-pink)' }}
                      />
                      <span className="lp-tile-text" style={{ fontSize: 12 }}>
                        I agree to receive interview text messages from Proxi at{' '}
                        {authPhone ? authPhone : 'the number above'}. Message frequency varies; message and data rates may apply. Reply STOP to opt out, HELP for help. See our{' '}
                        <a href="https://www.proxiaistudio.com/terms" target="_blank" rel="noopener noreferrer" className="lp-link">Terms</a> and{' '}
                        <a href="https://www.proxiaistudio.com/privacy" target="_blank" rel="noopener noreferrer" className="lp-link">Privacy Policy</a>.
                      </span>
                    </label>
                  </>
                )}

                {channelTile(
                  'APP',
                  'In the app',
                  authEmail
                    ? `Sign in here when you have time — we may also email ${authEmail}`
                    : 'Sign in here when you have time',
                  Mail,
                )}

                <button
                  type="button"
                  onClick={handleStep1Submit}
                  disabled={!isStep1Valid || isSubmitting}
                  className="lp-btn lp-btn-primary"
                  style={{ marginTop: 6 }}
                >
                  {isSubmitting ? <Loader2 style={{ width: 16, height: 16, animation: 'lp-spin 1s linear infinite' }} /> : (<><span>Next</span><ArrowRight style={{ width: 16, height: 16 }} /></>)}
                </button>
              </div>
            )}

            {/* Step 2 — Profile info */}
            {step === 2 && (
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{ display: 'flex', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label className="lp-field-label" htmlFor="first-name">First name</label>
                    <input
                      id="first-name"
                      type="text"
                      autoComplete="given-name"
                      value={firstName}
                      onChange={e => setFirstName(e.target.value)}
                      placeholder="Jane"
                      disabled={isSubmitting}
                      className="lp-input"
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label className="lp-field-label" htmlFor="last-name">Last name</label>
                    <input
                      id="last-name"
                      type="text"
                      autoComplete="family-name"
                      value={lastName}
                      onChange={e => setLastName(e.target.value)}
                      placeholder="Doe"
                      disabled={isSubmitting}
                      className="lp-input"
                    />
                  </div>
                </div>

                <div>
                  <label className="lp-field-label" htmlFor="occupation">Occupation</label>
                  <input
                    id="occupation"
                    type="text"
                    value={occupation}
                    onChange={e => setOccupation(e.target.value)}
                    placeholder="e.g. Software engineer, teacher, designer"
                    disabled={isSubmitting}
                    className="lp-input"
                  />
                </div>
                <div>
                  <label className="lp-field-label" htmlFor="zip">Zip code</label>
                  <input
                    id="zip"
                    type="text"
                    value={zipCode}
                    onChange={e => setZipCode(e.target.value)}
                    placeholder="e.g. 10001"
                    maxLength={10}
                    disabled={isSubmitting}
                    className="lp-input"
                  />
                </div>
                <div>
                  <label className="lp-field-label" htmlFor="desc">Describe what you do</label>
                  <textarea
                    id="desc"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    placeholder="Tell us about your work, expertise, or what you're passionate about…"
                    rows={3}
                    maxLength={500}
                    disabled={isSubmitting}
                    className="lp-textarea"
                  />
                  <p style={{
                    fontFamily: 'var(--lp-mono)',
                    fontSize: 10,
                    letterSpacing: '0.10em',
                    color: 'var(--lp-ink-mute)',
                    marginTop: 4,
                    textAlign: 'right',
                  }}>
                    {description.length}/500
                  </p>
                </div>

                <button
                  type="button"
                  onClick={handleStep2Submit}
                  disabled={!isStep2Valid || isSubmitting}
                  className="lp-btn lp-btn-primary"
                  style={{ marginTop: 6 }}
                >
                  {isSubmitting ? <Loader2 style={{ width: 16, height: 16, animation: 'lp-spin 1s linear infinite' }} /> : (<><span>Next</span><ArrowRight style={{ width: 16, height: 16 }} /></>)}
                </button>
              </div>
            )}

            {/* Step 3 — Photo */}
            {step === 3 && (
              <div style={{ marginTop: 22, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16 }}>
                <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoSelect} style={{ display: 'none' }} />

                {showCropper && rawPhotoUrl ? (
                  <>
                    <div
                      style={{
                        position: 'relative',
                        width: CROP_CONTAINER_SIZE,
                        height: CROP_CONTAINER_SIZE,
                        borderRadius: '50%',
                        overflow: 'hidden',
                        border: '2px solid var(--lp-line-strong)',
                        cursor: 'grab',
                        touchAction: 'none',
                      }}
                      onMouseDown={handleCropDragStart}
                      onMouseMove={handleCropDragMove}
                      onMouseUp={handleCropDragEnd}
                      onMouseLeave={handleCropDragEnd}
                      onTouchStart={handleCropDragStart}
                      onTouchMove={handleCropDragMove}
                      onTouchEnd={handleCropDragEnd}
                    >
                      <img
                        src={rawPhotoUrl}
                        alt="Crop preview"
                        draggable={false}
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          width: imgDimensions.width * (CROP_CONTAINER_SIZE / Math.min(imgDimensions.width, imgDimensions.height)) * zoom,
                          height: imgDimensions.height * (CROP_CONTAINER_SIZE / Math.min(imgDimensions.width, imgDimensions.height)) * zoom,
                          maxWidth: 'none',
                          transform: `translate(calc(-50% + ${offset.x}px), calc(-50% + ${offset.y}px))`,
                          userSelect: 'none',
                          pointerEvents: 'none',
                        }}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', maxWidth: 260 }}>
                      <ZoomOut style={{ width: 16, height: 16, color: 'var(--lp-ink-mute)' }} />
                      <input
                        type="range"
                        min="1"
                        max="3"
                        step="0.05"
                        value={zoom}
                        onChange={e => handleZoomChange(parseFloat(e.target.value))}
                        style={{ flex: 1, accentColor: 'var(--lp-pink)' }}
                      />
                      <ZoomIn style={{ width: 16, height: 16, color: 'var(--lp-ink-mute)' }} />
                    </div>

                    <p style={{ fontFamily: 'var(--lp-mono)', fontSize: 11, color: 'var(--lp-ink-mute)', letterSpacing: '0.08em' }}>
                      Drag to reposition · Slide to zoom
                    </p>

                    <div style={{ display: 'flex', gap: 10, width: '100%' }}>
                      <button
                        type="button"
                        onClick={() => { setShowCropper(false); setRawPhotoUrl(null); }}
                        className="lp-btn lp-btn-ghost"
                        style={{ flex: 1 }}
                      >
                        Cancel
                      </button>
                      <button type="button" onClick={handleCropConfirm} className="lp-btn lp-btn-primary" style={{ flex: 1 }}>
                        Confirm
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        if (fileInputRef.current) fileInputRef.current.value = '';
                        fileInputRef.current?.click();
                      }}
                      disabled={isSubmitting}
                      aria-label="Select profile photo"
                      style={{
                        width: 144, height: 144,
                        borderRadius: '50%',
                        border: '2px dashed var(--lp-line-strong)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        background: 'rgba(255,255,255,0.55)',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer',
                        transition: 'border-color 180ms ease, background 180ms ease, transform 180ms ease',
                        opacity: isSubmitting ? 0.7 : 1,
                      }}
                    >
                      {photoPreview ? (
                        <img src={photoPreview} alt="Profile preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, color: 'var(--lp-ink-mute)' }}>
                          <User style={{ width: 36, height: 36 }} />
                          <Camera style={{ width: 18, height: 18 }} />
                        </div>
                      )}
                    </button>

                    <p style={{ fontFamily: 'var(--lp-mono)', fontSize: 11, color: 'var(--lp-ink-mute)', letterSpacing: '0.08em' }}>
                      {photoPreview ? 'Tap to change photo' : 'Tap to upload or take a photo'}
                    </p>

                    <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                      {photoFile && (
                        <button type="button" onClick={handleComplete} disabled={isSubmitting} className="lp-btn lp-btn-primary">
                          {isSubmitting ? <Loader2 style={{ width: 16, height: 16, animation: 'lp-spin 1s linear infinite' }} /> : (<><span>Continue</span><ArrowRight style={{ width: 16, height: 16 }} /></>)}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => {
                          if (fileInputRef.current) fileInputRef.current.value = '';
                          photoFile ? fileInputRef.current?.click() : handleSkipPhoto();
                        }}
                        disabled={isSubmitting}
                        className="lp-btn lp-btn-ghost"
                      >
                        {isSubmitting && !photoFile ? (
                          <Loader2 style={{ width: 16, height: 16, animation: 'lp-spin 1s linear infinite' }} />
                        ) : photoFile ? 'Choose different photo' : 'Skip for now'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </main>
      </LightShell>
    </div>
  );
};

export default ContributorOnboarding;
